/** Encode mono Float32 PCM (-1..1) as a 16-bit PCM WAV and return base64 (KoboldCPP Whisper wants 16 kHz 16-bit WAV). */
export function encodeWavBase64(samples: Float32Array, sampleRate = 16000): string {
  const buffer = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buffer);
  const writeStr = (off: number, s: string) => {
    for (let i = 0; i < s.length; i++) view.setUint8(off + i, s.charCodeAt(i));
  };
  writeStr(0, 'RIFF');
  view.setUint32(4, 36 + samples.length * 2, true);
  writeStr(8, 'WAVE');
  writeStr(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeStr(36, 'data');
  view.setUint32(40, samples.length * 2, true);
  let off = 44;
  for (let i = 0; i < samples.length; i++, off += 2) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(off, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return bytesToBase64(new Uint8Array(buffer));
}

export function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunk)));
  }
  return btoa(binary);
}

export function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export function concatFloat32(chunks: Float32Array[]): Float32Array {
  const len = chunks.reduce((s, c) => s + c.length, 0);
  const out = new Float32Array(len);
  let off = 0;
  for (const c of chunks) {
    out.set(c, off);
    off += c.length;
  }
  return out;
}

export function rms(frame: Float32Array): number {
  let s = 0;
  for (let i = 0; i < frame.length; i++) s += frame[i] * frame[i];
  return Math.sqrt(s / Math.max(1, frame.length));
}

export function durationMs(samples: Float32Array, sampleRate = 16000): number {
  return (samples.length / sampleRate) * 1000;
}

/**
 * Keep only the audio recorded at or after `fromTs`. Used to drop the part of a VAD segment that
 * was already committed to the agent (segment started before the commit). Returns an empty
 * array when nothing remains.
 */
export function sliceAudioAfterWallTime(
  audio: Float32Array,
  startTs: number,
  endTs: number,
  fromTs: number,
  sampleRate = 16000,
): Float32Array {
  if (audio.length === 0 || !Number.isFinite(fromTs)) return audio;
  if (fromTs >= endTs) return new Float32Array(0);
  const spanMs = Math.max(1, endTs - startTs);
  const audioMs = (audio.length / sampleRate) * 1000;
  const padMs = Math.max(0, audioMs - spanMs);
  const audioOrigin = startTs - padMs;
  const dropMs = fromTs - audioOrigin;
  if (dropMs <= 0) return audio;
  const drop = Math.min(audio.length, Math.floor((dropMs / 1000) * sampleRate));
  return audio.subarray(drop);
}

/**
 * Drop the TTS-contaminated prefix of a barge-in capture. `startTs`/`endTs` are VAD onset/end;
 * the buffer may include pre-onset pad. Keeps `leadMs` before `fromTs` so the word that triggered
 * duck/pause is not clipped. Returns the original buffer if trimming would leave too little audio.
 *
 * Prefer trimming from duck/onset (not pause): pause is often 300–600 ms after the user started,
 * and trimming there cuts the first words of the interrupt.
 */
export function trimAudioFromWallTime(
  audio: Float32Array,
  startTs: number,
  endTs: number,
  fromTs: number,
  leadMs = 450,
  sampleRate = 16000,
): Float32Array {
  if (audio.length === 0 || !Number.isFinite(fromTs)) return audio;
  const spanMs = Math.max(1, endTs - startTs);
  const audioMs = (audio.length / sampleRate) * 1000;
  const padMs = Math.max(0, audioMs - spanMs);
  const audioOrigin = startTs - padMs;
  const keepFromMs = Math.max(0, fromTs - leadMs - audioOrigin);
  const drop = Math.floor((keepFromMs / 1000) * sampleRate);
  if (drop <= 0) return audio;
  const minKeep = Math.floor(0.35 * sampleRate);
  if (audio.length - drop < minKeep) return audio;
  return audio.subarray(drop);
}
